import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { HandHeart, Plus, Loader2, Heart, Pin, PinOff, Clock, Share2 } from "lucide-react";
import { relativeTime } from "@/lib/relativeTime";
import { waIntentionShareUrl } from "@/lib/share";

const pickLabel = (c, lang) => {
    if (!c) return "";
    const key = `label_${(lang || "en").slice(0, 2)}`;
    return c[key] || c.label_en || c.label || c.slug;
};

const prettifySlug = (slug) =>
    (slug || "")
        .split(/[_\-\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ") || "Intention";

export default function IntentionsBoard() {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const focusId = searchParams.get("focus");
    const focusRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [active, setActive] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", category: "other", is_anonymous: false });
    const [prayingId, setPrayingId] = useState(null);

    // When user opens the "New intention" form, default the category to the currently
    // selected filter chip (fall back to "other" when viewing "All intentions").
    const openForm = () => {
        setForm((f) => ({ ...f, category: active === "all" ? (f.category || "other") : active }));
        setShowForm((v) => !v);
    };

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const q = active === "all" ? {} : { params: { category: active } };
        api.get("/intentions", q).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
    }, [active]);

    // Scroll to focused intention when the URL carries ?focus=<id> and items are loaded
    useEffect(() => {
        if (!focusId || !items.length) return;
        const el = focusRef.current;
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [focusId, items]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user || !user.id) return;
        try {
            const { data } = await api.post("/intentions", form);
            setItems((prev) => [data, ...prev]);
            setForm({ title: "", description: "", category: "other", is_anonymous: false });
            setShowForm(false);
        } catch {
            // ignore
        }
    };

    const pray = async (id) => {
        if (!user || !user.id) {
            window.location.href = "/login";
            return;
        }
        setPrayingId(id);
        try {
            const { data } = await api.post(`/intentions/${id}/pray`);
            setItems((prev) => prev.map((x) => (x.id === id ? data : x)));
        } catch {
            /* ignore */
        } finally {
            setPrayingId(null);
        }
    };

    const togglePriority = async (i) => {
        if (user?.role !== "admin") return;
        try {
            const { data } = await api.put(`/intentions/${i.id}/priority`, { priority: !i.priority });
            setItems((prev) => prev.map((x) => (x.id === i.id ? data : x)));
        } catch { /* ignore */ }
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>Prayer Intention Board</div>
                        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>Let us carry one another.</h1>
                        <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>
                            Add your intention, or choose one already shared and press <em>I’m praying</em>. The Requester is notified each time.
                        </p>
                    </div>
                    <button onClick={openForm} className="btn-primary inline-flex items-center gap-2" data-testid="intentions-new-button">
                        <Plus size={16} /> New intention
                    </button>
                </div>

                {showForm && user?.id && (
                    <form onSubmit={submit} className="mt-6 p-card p-6 grid gap-4" data-testid="intentions-new-form">
                        <input
                            required
                            placeholder="Title — e.g. For my daughter’s 12th exam"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="w-full px-4 py-3 rounded-full border"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="intentions-new-title"
                        />
                        <textarea
                            placeholder="A short description (optional)"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 rounded-2xl border"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="intentions-new-description"
                        />
                        <div className="flex items-center gap-3 flex-wrap">
                            <select
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="intentions-new-category"
                            >
                                {categories.map((c) => (
                                    <option key={c.slug} value={c.slug}>{pickLabel(c, i18n.language)}</option>
                                ))}
                            </select>
                            <label className="text-sm flex items-center gap-2" style={{ color: "#1E1B4B" }}>
                                <input type="checkbox" checked={form.is_anonymous} onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })} data-testid="intentions-new-anonymous" />
                                Post anonymously
                            </label>
                            <button type="submit" className="btn-gold ml-auto text-sm" data-testid="intentions-new-submit">Share intention</button>
                        </div>
                    </form>
                )}

                <div className="mt-8 flex flex-wrap gap-2" data-testid="intentions-category-chips">
                    <button className={`chip ${active === "all" ? "active" : ""}`} onClick={() => setActive("all")} data-testid="intentions-chip-all">All intentions</button>
                    {categories.map((c) => (
                        <button key={c.slug} className={`chip ${active === c.slug ? "active" : ""}`} onClick={() => setActive(c.slug)} data-testid={`intentions-chip-${c.slug}`}>
                            {pickLabel(c, i18n.language)}
                        </button>
                    ))}
                </div>

                <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {loading && (
                        <div className="col-span-full flex items-center gap-2 text-sm" style={{ color: "#525252" }}>
                            <Loader2 className="animate-spin" size={16} /> Gathering intentions…
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="col-span-full p-card p-10 text-center">
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Be the first.</div>
                            <div className="text-sm mt-1" style={{ color: "#525252" }}>No intentions in this category yet.</div>
                        </div>
                    )}
                    {items.map((i, idx) => (
                        <div key={i.id}
                             ref={focusId === i.id ? focusRef : undefined}
                             className={`p-card p-5 fade-up delay-${(idx % 4) + 1}`}
                             style={focusId === i.id
                                 ? { border: "2px solid #D4AF37", boxShadow: "0 14px 40px -16px rgba(212,175,55,0.6)" }
                                 : (i.priority ? { border: "2px solid #D4AF37" } : {})}
                             data-testid={`intentions-card-${i.id}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full"
                                      style={i.priority ? { background: "#1E1B4B", color: "#D4AF37" } : { background: "#F5F0E6", color: "#7F1D1D" }}>
                                    {i.priority ? "Priority" : (pickLabel(categories.find((c) => c.slug === i.category), i18n.language) || prettifySlug(i.category))}
                                </span>
                                <span className="text-xs" style={{ color: "#525252" }}>{i.requester_name}</span>
                            </div>
                            <div className="font-display text-2xl mt-3" style={{ color: "#1E1B4B" }}>{i.title}</div>
                            {i.description && <div className="text-sm mt-2" style={{ color: "#525252" }}>{i.description}</div>}
                            <div className="mt-2 text-xs inline-flex items-center gap-1" style={{ color: "#8a8a8a" }} data-testid={`intentions-card-time-${i.id}`}>
                                <Clock size={11} /> {relativeTime(i.created_at)}
                            </div>
                            <div className="mt-5 flex items-center justify-between">
                                <div className="text-sm flex items-center gap-1.5" style={{ color: "#7F1D1D" }}>
                                    <Heart size={14} /> {i.prayer_count} praying
                                </div>
                                <div className="flex items-center gap-2">
                                    {user?.role === "admin" && (
                                        <button onClick={() => togglePriority(i)}
                                                className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1"
                                                style={{ borderColor: "#D4AF37", color: i.priority ? "#7F1D1D" : "#1E1B4B" }}
                                                title={i.priority ? "Unpin from priority" : "Promote to priority"}
                                                data-testid={`intentions-pin-${i.id}`}>
                                            {i.priority ? <><PinOff size={12} /> Unpin</> : <><Pin size={12} /> Promote</>}
                                        </button>
                                    )}
                                    <a
                                        href={waIntentionShareUrl({
                                            title: i.title,
                                            description: i.description,
                                            requesterName: i.requester_name,
                                            intentionId: i.id,
                                        })}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5"
                                        style={{ borderColor: "#25D366", color: "#1E1B4B", background: "#FDFBF7" }}
                                        title={t("priority.share_wa")}
                                        data-testid={`intentions-share-wa-${i.id}`}
                                    >
                                        <Share2 size={12} style={{ color: "#25D366" }} /> {t("priority.share_wa")}
                                    </a>
                                    <button onClick={() => pray(i.id)} disabled={prayingId === i.id}
                                            className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                                            data-testid={`intentions-pray-${i.id}`}>
                                        <HandHeart size={14} /> {prayingId === i.id ? "Praying…" : "I’m praying"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
